import React, { useState, useEffect, useMemo } from "react";
import ContactVendorleadsModal from "../../components/ContactVendorleadsModal";
import SignInModal from "../../components/SignInModal";
import Navbar from "../../components/Navbar";
import TestimonialSection from "../../components/TestimonialSection";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";
const BRAND_BLUE = "#3b82f6";
const GRAY_DARK_COLOR = "#374151";

const SAMPLE_REPORT_URL = "https://www.homeprice.site/appraisal/apr_67123e152e8a";

/* ─────────── Demo data: live appraisal apr_67123e152e8a (demo-001 agency) ─────────── */
const DEMO_AGENCY = {
  agency_name: "Demo Agency",
  agent_name: "John Doe",
  agent_first_name: "John",
  agent_title: "Licensed Agent",
  agent_email: "john@demoagency.com.au",
  agent_phone: "0400 000 000",
  agent_colour: "#0f172a",
  logo_url:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/logo_url_1774836185_14b54735.png",
  profile_image_url:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/profile-image.jpg",
};
const AGENT_COLOUR = DEMO_AGENCY.agent_colour;

const SUBJECT = {
  street: "6 Golf Parade",
  suburb: "Manly",
  state: "NSW",
  postcode: "2095",
  beds: 2,
  baths: 1,
  cars: 0,
  land: 187,
  photo: "https://b.domainstatic.com.au/2020383814_1_1_251030_010315-w2000-h1334",
};

const PRICE_LOW = 2_300_000;
const PRICE_HIGH = 2_700_000;
const RANGE_MIN = 1_800_000;
const RANGE_MAX = 4_500_000;

/* Top 3 comps are real (apr_67123e152e8a); c4–c6 are weak outliers for the picking animation. */
const COMPS_POOL = [
  { id: "c1", addr: "75 Whistler Street", sub: "Manly", beds: 2, baths: 1, cars: 0, land: 219, price: 4_200_000, sold: "Sold 4 weeks ago",
    photo: "https://rimh2.domainstatic.com.au/IBBnHxWK1khNBhyyxk672qPlisw=/640x480/filters:format(jpeg):quality(80):no_upscale()/2020403513_1_1_251106_052026-w1600-h1068",
    distance: 0.2, score: 88, matchStars: 4,
    aiSummary: "Very similar comparable. Same 2 bed / 1 bath / no parking. Land is 17% larger (219m² vs 187m²), keeping it just short of a near-identical match." },
  { id: "c2", addr: "94 Whistler Street", sub: "Manly", beds: 2, baths: 1, cars: 1, land: 164, price: 3_350_000, sold: "Sold 9 weeks ago",
    photo: "https://rimh2.domainstatic.com.au/8wag3H8mCdad_KQXQ8CPf5POX7M=/640x480/filters:format(jpeg):quality(80):no_upscale()/2020297180_1_1_250925_061031-w3464-h2309",
    distance: 0.3, score: 86, matchStars: 4,
    aiSummary: "Strong reference. Also 2 bed / 1 bath in Manly, one car space, land 12% smaller. Sale price $3.35m anchors the upper end." },
  { id: "c3", addr: "50A Smith Street", sub: "Manly", beds: 2, baths: 1, cars: 1, land: 112, price: 2_800_000, sold: "Sold 14 weeks ago",
    photo: "https://rimh2.domainstatic.com.au/Rtb9vf2DtXTY-OwAiSWana-NAug=/640x480/filters:format(jpeg):quality(80):no_upscale()/2020264111_1_1_250912_010810-w1600-h1066",
    distance: 0.5, score: 72, matchStars: 3,
    aiSummary: "Reasonable comparable. Same bed/bath, extra car. Land is 40% smaller (112m² vs 187m²), a material difference that weakens the comparison." },
  { id: "c4", addr: "12 Pittwater Road", sub: "Manly", beds: 4, baths: 3, cars: 2, land: 412, price: 4_800_000, sold: "Sold 2 weeks ago",
    photo: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=70",
    distance: 0.4, score: 41, matchStars: 1,
    aiSummary: "Much larger dwelling on a 412m² block. Not a like-for-like comparison." },
  { id: "c5", addr: "8 Raglan Street", sub: "Manly", beds: 1, baths: 1, cars: 0, land: 95, price: 1_950_000, sold: "Sold 7 weeks ago",
    photo: "https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?w=800&q=70",
    distance: 0.6, score: 58, matchStars: 2,
    aiSummary: "Smaller 1-bed dwelling on a 95m² block. Useful as a floor but not a peer." },
  { id: "c6", addr: "22 Ocean View Crescent", sub: "Freshwater", beds: 2, baths: 1, cars: 1, land: 170, price: 2_450_000, sold: "Sold 12 weeks ago",
    photo: "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=800&q=70",
    distance: 2.1, score: 49, matchStars: 2,
    aiSummary: "Similar dwelling profile but different suburb. Used as a distant reference only." },
];

const fmtPrice = (n) => {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return "$" + (Math.round(m * 100) / 100).toFixed(m % 1 === 0 ? 1 : 2) + "m";
  }
  return "$" + Math.round(n / 1000) + "k";
};

/* AI image suggestion (image 1 of apr_67123e152e8a). */
const AI_SUGGESTION = {
  imageUrl: SUBJECT.photo,
  overall:
    "The property has a charming facade but could benefit from minor updates to enhance its curb appeal. The biggest opportunities lie in refreshing the entrance and modernising the outdoor elements.",
  quickWins: [
    { action: "Clean and paint the front door and window frames", impact: "Enhances curb appeal and gives a fresh look", cost: "$100–$200" },
    { action: "Add a welcome mat and potted plants", impact: "Creates a welcoming entrance", cost: "$50–$100" },
  ],
  moderate: [
    { action: "Refresh the front garden bed with low-maintenance natives", impact: "Modernises the facade for very little spend", cost: "$400–$800" },
  ],
  investment: "$2,050–$4,100",
  uplift: "$15,000–$25,000",
};

/* Location analysis content from features.location_analysis. */
const LOCATION_ANALYSIS = {
  summary:
    "6 Golf Parade, Manly is in a prime beachside pocket, one street back from Queenscliff Beach and close to Pittwater Road amenities.",
  positives: [
    "Very close to Queenscliff Beach",
    "Walkable to cafes and local shops",
    "Near Pittwater Road transport",
    "Positioned on a quieter side street",
    "Strong Manly beachside lifestyle appeal",
  ],
  negatives: [
    "Close to Pittwater Road traffic corridor",
    "Slightly less direct beach frontage than Whistler Street comparables",
    "Potential visitor and parking pressure in peak beach periods",
  ],
  valueFactors: [
    { factor: "Beach proximity", impact: "positive", detail: "Short walk to Queenscliff Beach, supporting strong owner-occupier and holiday appeal." },
    { factor: "Street quietness", impact: "positive", detail: "Golf Parade is calmer than Pittwater Road and benefits from a more residential setting." },
    { factor: "Transport and shops", impact: "positive", detail: "Immediate access to Pittwater Road buses and nearby retail broadens buyer appeal." },
    { factor: "Not on prime Whistler Street strip", impact: "negative", detail: "Whistler Street sales appear slightly more premium due to closer beachside position." },
    { factor: "Main road proximity", impact: "negative", detail: "Being near Pittwater Road may introduce some traffic noise compared with quieter inland streets." },
  ],
  amenities: [
    { name: "Queenscliff Beach", type: "beach", distance: "approx. 250m walk", icon: "🏖" },
    { name: "Pittwater Road bus stops", type: "transport", distance: "approx. 100m walk", icon: "🚌" },
    { name: "Ruby Lane Wholefoods Cafe", type: "cafe", distance: "approx. 200m walk", icon: "☕" },
    { name: "Love Is... Jewellery", type: "shopping", distance: "approx. 120m walk", icon: "🛍" },
    { name: "Manly Surf School", type: "activity", distance: "approx. 550m walk", icon: "🏄" },
  ],
};

/* ─────────── Keyframes ─────────── */
function GlobalAnimations() {
  return (
    <style>{`
      @keyframes fadeUp { 0%{opacity:0;transform:translateY(10px);} 100%{opacity:1;transform:translateY(0);} }
      @keyframes fadeIn { 0%{opacity:0;} 100%{opacity:1;} }
      @keyframes slideInDown { 0%{opacity:0;transform:translateY(-12px);} 100%{opacity:1;transform:translateY(0);} }
      @keyframes slideInLeft { 0%{opacity:0;transform:translateX(-12px);} 100%{opacity:1;transform:translateX(0);} }
      @keyframes pulseGlow { 0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,0.35);} 50%{box-shadow:0 0 0 12px rgba(16,185,129,0);} }
      @keyframes barGrow { 0%{transform:scaleY(0.05);} 100%{transform:scaleY(1);} }
      @keyframes tickPop { 0%{transform:scale(0);opacity:0;} 60%{transform:scale(1.2);opacity:1;} 100%{transform:scale(1);opacity:1;} }
      @keyframes spinSlow { 0%{transform:rotate(0);} 100%{transform:rotate(360deg);} }
      @keyframes blink { 0%,49%{opacity:1;} 50%,100%{opacity:0;} }
      @keyframes caretBlink { 0%,49%{opacity:1;} 50%,100%{opacity:0;} }
      @keyframes shimmer { 0%{background-position:-200px 0;} 100%{background-position:200px 0;} }
      @keyframes eyeScan { 0%,100%{transform:translateY(0);} 50%{transform:translateY(6px);} }
    `}</style>
  );
}

/* ─────────── Atoms ─────────── */
function Chip({ icon, label, colour = BRAND_ACCENT_COLOR }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 999, background: `${colour}1f`, color: colour, fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}>
      {icon && <span style={{ fontSize: 10 }}>{icon}</span>}
      {label}
    </div>
  );
}

function SectionHeading({ kicker, title, blurb, accent }) {
  return (
    <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 2.5rem auto" }}>
      <div style={{ display: "inline-block", fontSize: 11, fontWeight: 800, letterSpacing: 2, color: accent, background: `${accent}1a`, padding: "4px 12px", borderRadius: 999, marginBottom: 12, textTransform: "uppercase" }}>
        {kicker}
      </div>
      <h2 style={{ fontSize: "1.9rem", fontWeight: 800, color: BRAND_COLOR, margin: "0 0 0.75rem 0", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
        {title}
      </h2>
      <p style={{ color: GRAY_DARK_COLOR, fontSize: "1rem", lineHeight: 1.65, margin: 0 }}>{blurb}</p>
    </div>
  );
}

function MatchStars({ stars, size = 11, colour = BRAND_ACCENT_COLOR }) {
  return (
    <span style={{ display: "inline-flex", gap: 1.5 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} style={{ fontSize: size, color: n <= stars ? colour : "#e2e8f0", lineHeight: 1 }}>★</span>
      ))}
    </span>
  );
}

/* ─────────── Subject hero (used inside report) ─────────── */
function SubjectHero({ compact }) {
  return (
    <div style={{ borderRadius: 10, overflow: "hidden", background: "#0f172a", border: "1px solid #1f2937", position: "relative" }}>
      <img src={SUBJECT.photo} alt={SUBJECT.street} style={{ width: "100%", height: compact ? 110 : 160, objectFit: "cover", display: "block" }} />
      <div style={{ position: "absolute", left: 10, bottom: 10, right: 10, color: "#fff" }}>
        <div style={{ fontSize: compact ? 12 : 14, fontWeight: 800 }}>{SUBJECT.street}</div>
        <div style={{ fontSize: compact ? 9.5 : 11, opacity: 0.85, letterSpacing: 0.4 }}>
          {SUBJECT.suburb.toUpperCase()} · {SUBJECT.state} {SUBJECT.postcode}
        </div>
      </div>
      <div style={{ position: "absolute", right: 8, top: 8, background: "rgba(0,0,0,0.6)", color: "#fff", padding: "3px 8px", borderRadius: 12, fontSize: 9.5, fontWeight: 700 }}>
        {SUBJECT.beds} Bed · {SUBJECT.baths} Bath · {SUBJECT.cars} Car
      </div>
    </div>
  );
}

/* ─────────── Hero live builder ─────────── */
function HeroAppraisalBuilder({ isMobile }) {
  const [phase, setPhase] = useState("picking");
  const [pickedIds, setPickedIds] = useState([]);
  const pickOrder = useMemo(() => ["c1", "c2", "c3"], []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      while (mounted) {
        setPhase("picking");
        setPickedIds([]);
        for (let i = 0; i < pickOrder.length; i++) {
          await new Promise((r) => setTimeout(r, 850));
          if (!mounted) return;
          setPickedIds((prev) => [...prev, pickOrder[i]]);
        }
        await new Promise((r) => setTimeout(r, 700));
        if (!mounted) return;
        setPhase("narrowing");
        await new Promise((r) => setTimeout(r, 1800));
        if (!mounted) return;
        setPhase("assembling");
        await new Promise((r) => setTimeout(r, 2000));
        if (!mounted) return;
        setPhase("done");
        await new Promise((r) => setTimeout(r, 3000));
      }
    };
    run();
    return () => { mounted = false; };
  }, [pickOrder]);

  const pickedComps = COMPS_POOL.filter((c) => pickedIds.includes(c.id));

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.05fr 1fr", gap: isMobile ? 18 : 26, alignItems: "stretch" }}>
      <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: isMobile ? 14 : 18, position: "relative", overflow: "hidden", minHeight: 380 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase" }}>
            Step 1 · Selecting comparables
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: BRAND_ACCENT_COLOR, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: BRAND_ACCENT_COLOR, boxShadow: `0 0 0 4px ${BRAND_ACCENT_COLOR}33` }} />
            {pickedIds.length}/3 picked
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {COMPS_POOL.slice(0, 6).map((c) => {
            const picked = pickedIds.includes(c.id);
            const good = c.score >= 70;
            return (
              <div key={c.id} style={{ background: picked ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.04)", border: `1.5px solid ${picked ? BRAND_ACCENT_COLOR : good ? "rgba(255,255,255,0.12)" : "rgba(248,113,113,0.25)"}`, borderRadius: 10, overflow: "hidden", transition: "all 0.4s ease", opacity: !good && phase !== "picking" ? 0.35 : 1, position: "relative", transform: picked ? "scale(1.01)" : "scale(1)" }}>
                <div style={{ position: "relative", height: 70 }}>
                  <img src={c.photo} alt={c.addr} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <div style={{ position: "absolute", top: 6, right: 6, background: good ? "rgba(16,185,129,0.95)" : "rgba(248,113,113,0.9)", color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 999, letterSpacing: 0.4 }}>
                    {c.score}% MATCH
                  </div>
                  {picked && (
                    <div style={{ position: "absolute", left: 6, top: 6, width: 22, height: 22, borderRadius: "50%", background: BRAND_ACCENT_COLOR, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, animation: "tickPop 0.45s ease both", boxShadow: "0 4px 10px rgba(0,0,0,0.4)" }}>
                      ✓
                    </div>
                  )}
                </div>
                <div style={{ padding: "7px 9px 9px 9px" }}>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.addr}</div>
                  <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>
                    {c.beds}·{c.baths}·{c.cars} · {c.land}m² · {c.distance}km
                  </div>
                  <div style={{ marginTop: 5, fontSize: 12.5, fontWeight: 800, color: picked ? BRAND_ACCENT_COLOR : "#fff" }}>{fmtPrice(c.price)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: isMobile ? 14 : 20, display: "flex", flexDirection: "column", minHeight: 380, position: "relative", overflow: "hidden" }}>
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 12 }}>
          Step 2 · Narrowing the range
        </div>
        <SubjectHero compact />
        <div style={{ marginTop: 16, flex: 1, display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginBottom: 6, letterSpacing: 0.5 }}>Estimated sale price</div>
          <div style={{ fontSize: isMobile ? "1.4rem" : "1.7rem", fontWeight: 900, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            {fmtPrice(PRICE_LOW)} <span style={{ color: BRAND_ACCENT_COLOR }}>to</span> {fmtPrice(PRICE_HIGH)}
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 4 }}>
            Mid-point {fmtPrice((PRICE_LOW + PRICE_HIGH) / 2)} · based on {pickedIds.length} recent sales
          </div>
          <div style={{ marginTop: 18, height: 70, position: "relative" }}>
            <div style={{ position: "absolute", left: 0, right: 0, top: 30, height: 8, borderRadius: 4, background: "rgba(255,255,255,0.08)" }} />
            <div style={{ position: "absolute", top: 26, height: 16, borderRadius: 8, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_ACCENT_COLOR})`, boxShadow: `0 0 24px ${BRAND_ACCENT_COLOR}66`,
              left: phase === "picking" ? "5%" : `${((PRICE_LOW - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * 100}%`,
              right: phase === "picking" ? "5%" : `${(1 - (PRICE_HIGH - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * 100}%`,
              transition: "left 1.4s cubic-bezier(.65,.05,.36,1), right 1.4s cubic-bezier(.65,.05,.36,1)" }} />
            <div style={{ position: "absolute", top: 14, left: `${(((PRICE_LOW + PRICE_HIGH) / 2 - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * 100}%`, transform: "translateX(-50%)", width: 2, height: 40, background: "rgba(255,255,255,0.55)" }} />
            <div style={{ position: "absolute", top: 0, left: `${(((PRICE_LOW + PRICE_HIGH) / 2 - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * 100}%`, transform: "translateX(-50%)", color: "#fff", fontSize: 10, fontWeight: 800, background: BRAND_COLOR, padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap" }}>
              MID {fmtPrice((PRICE_LOW + PRICE_HIGH) / 2)}
            </div>
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "space-between", fontSize: 9.5, color: "rgba(255,255,255,0.45)" }}>
              <span>{fmtPrice(RANGE_MIN)}</span><span>{fmtPrice((RANGE_MIN + RANGE_MAX) / 2)}</span><span>{fmtPrice(RANGE_MAX)}</span>
            </div>
          </div>
          <div style={{ position: "relative", marginTop: -6, height: 24 }}>
            {pickedComps.map((c) => {
              const pct = ((c.price - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * 100;
              const clamped = Math.max(0, Math.min(100, pct));
              return (
                <div key={c.id} style={{ position: "absolute", left: `${clamped}%`, top: 0, transform: "translateX(-50%)", animation: "fadeUp 0.4s ease both" }}>
                  <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", border: `3px solid ${BRAND_ACCENT_COLOR}` }} />
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Chip icon="⚡" label={phase === "picking" ? "ANALYSING" : "TIGHTENED"} colour={BRAND_ACCENT_COLOR} />
            <Chip icon="📍" label="LOCAL DATA" colour={BRAND_BLUE} />
            <Chip icon="🎯" label="±5% CONFIDENCE" colour="#a78bfa" />
          </div>
        </div>
        {(phase === "assembling" || phase === "done") && (
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,23,42,0.92), rgba(15,23,42,0.98))", display: "flex", alignItems: "center", justifyContent: "center", animation: "fadeIn 0.4s ease both", padding: 16 }}>
            <div style={{ textAlign: "center" }}>
              {phase === "assembling" ? (
                <>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", border: "4px solid rgba(255,255,255,0.15)", borderTopColor: BRAND_ACCENT_COLOR, margin: "0 auto 14px", animation: "spinSlow 1s linear infinite" }} />
                  <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>Assembling appraisal report.</div>
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 4 }}>Branding · charts · comparables · agent details</div>
                </>
              ) : (
                <>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", background: BRAND_ACCENT_COLOR, margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, color: "#fff", fontWeight: 900, animation: "tickPop 0.5s ease both", boxShadow: `0 0 40px ${BRAND_ACCENT_COLOR}88` }}>✓</div>
                  <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>Report ready</div>
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 4 }}>7 minutes · zero spreadsheets</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────── Branded appraisal report ─────────── */
function AppraisalReport({ width = 360, withShadow = true }) {
  return (
    <div style={{ width, background: "#fff", borderRadius: 20, overflow: "hidden", border: "1px solid #efece8", boxShadow: withShadow ? "0 30px 70px rgba(15,23,42,0.45)" : "none", color: "#1a1a1a" }}>
      <div style={{ background: AGENT_COLOUR, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <img src={DEMO_AGENCY.logo_url} alt="Demo Agency" style={{ maxHeight: 22, maxWidth: 110, objectFit: "contain", filter: "brightness(0) invert(1)" }} />
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>Property Appraisal</div>
      </div>
      <div style={{ position: "relative", height: 170, background: `linear-gradient(165deg, ${AGENT_COLOUR} 0%, ${AGENT_COLOUR}dd 40%, #1a1a1a 100%)` }}>
        <img src={SUBJECT.photo} alt={SUBJECT.street} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.78, display: "block" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7))" }} />
        <div style={{ position: "absolute", left: 14, bottom: 12, right: 14, color: "#fff" }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", color: "rgba(255,255,255,0.6)", textTransform: "uppercase", marginBottom: 4 }}>Property Appraisal</div>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em" }}>{SUBJECT.street}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.78)", marginTop: 2, fontWeight: 500 }}>{SUBJECT.suburb}, {SUBJECT.state} {SUBJECT.postcode}</div>
        </div>
      </div>
      <div style={{ display: "flex", borderBottom: "1px solid #f0eeeb", background: "#fafaf8" }}>
        {[["Beds", SUBJECT.beds], ["Baths", SUBJECT.baths], ["Cars", SUBJECT.cars], ["Land", `${SUBJECT.land}m²`]].map(([k, v]) => (
          <div key={k} style={{ flex: 1, textAlign: "center", padding: "9px 4px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a" }}>{v}</div>
            <div style={{ fontSize: 9, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginTop: 1 }}>{k}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: "14px 16px 10px" }}>
        <div style={{ fontSize: 9.5, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8, borderLeft: `3px solid ${BRAND_ACCENT_COLOR}`, paddingLeft: 8 }}>Estimated Value</div>
        <div style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)", borderRadius: 14, padding: "14px 16px", color: "#fff" }}>
          <div style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: "0.12em", color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Estimated Range</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, letterSpacing: "-0.02em" }}>
            {fmtPrice(PRICE_LOW)} <span style={{ color: BRAND_ACCENT_COLOR, fontWeight: 700 }}>to</span> {fmtPrice(PRICE_HIGH)}
          </div>
          <div style={{ marginTop: 10, height: 5, background: "rgba(255,255,255,0.12)", borderRadius: 4, position: "relative" }}>
            <div style={{ position: "absolute", left: `${((PRICE_LOW - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * 100}%`, right: `${(1 - (PRICE_HIGH - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * 100}%`, top: 0, bottom: 0, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_ACCENT_COLOR})`, borderRadius: 4 }} />
          </div>
          <div style={{ fontSize: 9.5, marginTop: 6, color: "rgba(255,255,255,0.5)", display: "flex", justifyContent: "space-between" }}>
            <span>{fmtPrice(RANGE_MIN)}</span><span>{fmtPrice(RANGE_MAX)}</span>
          </div>
        </div>
      </div>
      <div style={{ padding: "8px 16px 14px" }}>
        <div style={{ fontSize: 9.5, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8, borderLeft: `3px solid ${BRAND_ACCENT_COLOR}`, paddingLeft: 8 }}>Recent Comparable Sales</div>
        {COMPS_POOL.slice(0, 3).map((c) => (
          <div key={c.id} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #f5f4f1", alignItems: "center" }}>
            <img src={c.photo} alt={c.addr} style={{ width: 46, height: 38, objectFit: "cover", borderRadius: 8 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", letterSpacing: "-0.01em" }}>{c.addr}</div>
              <div style={{ fontSize: 9.5, color: "#9ca3af", marginTop: 1 }}>{c.beds} bed · {c.baths} bath · {c.cars} car · {c.sold}</div>
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.01em" }}>{fmtPrice(c.price)}</div>
          </div>
        ))}
      </div>
      <div style={{ background: "#fafaf8", borderTop: "1px solid #f0eeeb", padding: "12px 16px", display: "flex", alignItems: "center", gap: 11 }}>
        <img src={DEMO_AGENCY.profile_image_url} alt={DEMO_AGENCY.agent_name} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", background: "#e5e7eb", flexShrink: 0, border: `2px solid ${BRAND_ACCENT_COLOR}33` }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.01em" }}>{DEMO_AGENCY.agent_name}</div>
          <div style={{ fontSize: 10, color: "#6b7280", marginTop: 1 }}>{DEMO_AGENCY.agent_title} · {DEMO_AGENCY.agency_name}</div>
          <div style={{ fontSize: 9.5, color: "#374151", marginTop: 2 }}>{DEMO_AGENCY.agent_phone} · {DEMO_AGENCY.agent_email}</div>
        </div>
      </div>
      <div style={{ padding: "12px 16px 16px", borderTop: "1px solid #f0eeeb" }}>
        <div style={{ background: BRAND_ACCENT_COLOR, color: "#fff", padding: "11px 14px", borderRadius: 14, fontWeight: 700, fontSize: 12.5, letterSpacing: 0.2, textAlign: "center", boxShadow: "0 6px 16px rgba(16,185,129,0.35)" }}>
          Book a meeting with {DEMO_AGENCY.agent_first_name}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Hero ─────────── */
function Hero({ isMobile, onCta }) {
  return (
    <section style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)", padding: isMobile ? "2.5rem 1.25rem 3.5rem" : "4.5rem 2rem 5rem", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "60px 60px", pointerEvents: "none" }} />
      <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.35)", color: "#6ee7b7", fontSize: "0.85rem", fontWeight: 600, borderRadius: 999, padding: "0.4rem 1rem", marginBottom: "1.1rem", letterSpacing: "0.03em" }}>
            Property Appraisal · CMA in minutes
          </div>
          <h1 style={{ fontSize: isMobile ? "2rem" : "3rem", fontWeight: 800, lineHeight: 1.1, color: "#fff", letterSpacing: "-0.02em", maxWidth: 880, margin: "0 auto 0.85rem" }}>
            Win the listing with a beautiful appraisal, <span style={{ color: BRAND_ACCENT_COLOR }}>not a spreadsheet</span>
          </h1>
          <p style={{ fontSize: isMobile ? "1rem" : "1.15rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.6, maxWidth: 660, margin: "0 auto" }}>
            Pick the best comparable sales, tighten the range, and send a branded report your vendor actually wants to open, in the time it used to take to find a parking spot.
          </p>
        </div>
        <HeroAppraisalBuilder isMobile={isMobile} />
        <div style={{ marginTop: 32, display: isMobile ? "grid" : "flex", gridTemplateColumns: isMobile ? "1fr 1fr" : undefined, gap: 12, justifyContent: "center", maxWidth: isMobile ? 420 : undefined, marginLeft: "auto", marginRight: "auto" }}>
          <button onClick={onCta} style={{ border: "none", borderRadius: 8, padding: "0.95rem 1.5rem", fontSize: "1rem", fontWeight: 700, cursor: "pointer", background: BRAND_ACCENT_COLOR, color: "#fff", boxShadow: "0 4px 14px rgba(16,185,129,0.4)", transition: "transform 0.15s, box-shadow 0.15s", flex: isMobile ? undefined : "0 1 240px", width: isMobile ? "100%" : undefined }}
            onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.55)"; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)"; }}>
            Start your first appraisal
          </button>
          <a href={SAMPLE_REPORT_URL} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "0.95rem 1.5rem", fontSize: "1rem", fontWeight: 600, background: "rgba(255,255,255,0.05)", color: "#fff", flex: isMobile ? undefined : "0 1 240px", width: isMobile ? "100%" : undefined, boxSizing: "border-box" }}>
            See a sample report <span style={{ fontSize: 13, opacity: 0.8 }}>↗</span>
          </a>
        </div>
        <div style={{ marginTop: 36, display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 14, maxWidth: 880, marginLeft: "auto", marginRight: "auto" }}>
          {[["~7 min", "Average build time"], ["100%", "Agency-branded"], ["Mobile", "Vendor-friendly view"], ["AI", "Comps + photo scoring"]].map(([k, v]) => (
            <div key={v} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "14px 16px", textAlign: "center" }}>
              <div style={{ fontSize: "1.4rem", fontWeight: 900, color: BRAND_ACCENT_COLOR, letterSpacing: "-0.02em" }}>{k}</div>
              <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.65)", marginTop: 2, letterSpacing: 0.3 }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Before / After ─────────── */
function BeforeAfter({ isMobile }) {
  return (
    <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", background: "#fff" }}>
      <SectionHeading kicker="The old way" accent={BRAND_ACCENT_COLOR} title="Most agents lose hours to the same spreadsheet" blurb="Pulling comps from three different portals, screenshotting photos, fudging fonts in Word, exporting a PDF that still looks like a 2007 newsletter. Then you send it and hope." />
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 22 }}>
        <div style={{ borderRadius: 16, border: "1px solid #fecaca", background: "#fff5f5", padding: isMobile ? 18 : 24, position: "relative" }}>
          <div style={{ display: "inline-block", background: "#dc2626", color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 999, letterSpacing: 0.5, marginBottom: 10 }}>BEFORE</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#7f1d1d", marginBottom: 10 }}>3 hours, two coffees, no charm</div>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 11.5, color: "#374151", lineHeight: 1.7 }}>
            <div>🟥 Open portal 1, search comps</div>
            <div>🟥 Open portal 2, copy sale prices</div>
            <div>🟥 Screenshot 6 photos at wrong res</div>
            <div>🟥 Glue into Word doc, fight margins</div>
            <div>🟥 Export ugly PDF</div>
            <div>🟥 Attach to email, hope vendor opens</div>
            <div>🟥 Realise you forgot the agent photo</div>
            <div>🟥 Start again</div>
          </div>
          <div style={{ marginTop: 14, padding: "10px 12px", background: "#fee2e2", borderRadius: 8, fontSize: 12.5, color: "#7f1d1d", fontWeight: 600 }}>
            ⏱ 3hr · vendor opens it once · never books a meeting
          </div>
        </div>
        <div style={{ borderRadius: 16, border: "1px solid rgba(16,185,129,0.3)", background: "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)", padding: isMobile ? 18 : 24, position: "relative" }}>
          <div style={{ display: "inline-block", background: BRAND_ACCENT_COLOR, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 999, letterSpacing: 0.5, marginBottom: 10 }}>WITH VENDORLEADS</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#065f46", marginBottom: 10 }}>7 minutes, one tap, one polished report</div>
          <div style={{ background: "#fff", border: "1px solid #d1fae5", borderRadius: 10, padding: 14, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 11.5, color: "#064e3b", lineHeight: 1.7 }}>
            <div>🟢 Search the address, comps load instantly</div>
            <div>🟢 Pick best 3 to 5, AI scores each match</div>
            <div>🟢 AI analyses every photo, suggests improvements</div>
            <div>🟢 Location analysis writes itself</div>
            <div>🟢 Range auto-narrows on screen</div>
            <div>🟢 Send via SMS or email link</div>
            <div>🟢 Vendor opens, taps "book a meeting"</div>
            <div>🟢 Win the listing</div>
          </div>
          <div style={{ marginTop: 14, padding: "10px 12px", background: "#d1fae5", borderRadius: 8, fontSize: 12.5, color: "#065f46", fontWeight: 600 }}>
            ⚡ 7min · vendor opens 4× · books in 2 days
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Report anatomy ─────────── */
function ReportAnatomy({ isMobile }) {
  return (
    <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", background: "#f8fafc", borderTop: "1px solid #eef2f7", borderBottom: "1px solid #eef2f7" }}>
      <SectionHeading kicker="The report" accent={BRAND_ACCENT_COLOR} title="Looks like you spent a week on it" blurb="Agency-branded header, vendor's own photo, a confident price range, and just the right number of comparables. No clutter." />
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "auto 1fr", gap: isMobile ? 30 : 56, alignItems: "center" }}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <AppraisalReport width={isMobile ? 300 : 360} />
        </div>
        <div>
          <h3 style={{ fontSize: "1.3rem", fontWeight: 700, color: BRAND_COLOR, margin: "0 0 14px 0" }}>Every section, dialled in</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
            {[
              ["Agency header", "Your logo, your brand colour, every page"],
              ["Vendor hero", "Their own house photo, full screen"],
              ["Quick stats", "Beds, baths, cars, land at a glance"],
              ["Range card", "Confident low to high with mid-point"],
              ["Comparables", "Match score, AI summary, sale price"],
              ["Location analysis", "Suburb summary, positives, amenities"],
              ["Improvements", "Per-photo quick wins and value uplift"],
              ["Agent footer", "Photo, mobile, email, one tap to call"],
            ].map(([t, d]) => (
              <div key={t} style={{ background: "#fff", borderRadius: 10, padding: "11px 13px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: BRAND_BLUE }}>{t}</div>
                <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{d}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 18 }}>
            <a href={SAMPLE_REPORT_URL} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", border: `1.5px solid ${BRAND_COLOR}`, borderRadius: 8, padding: "0.7rem 1.3rem", background: "#fff", color: BRAND_COLOR, fontWeight: 700, fontSize: "0.9rem" }}>
              See a live sample report <span style={{ fontSize: 12 }}>↗</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Comparable matching: AI eye + score + summary ─────────── */
function ComparableMatchSection({ isMobile }) {
  const [scanIdx, setScanIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setScanIdx((i) => (i + 1) % 3), 2200);
    return () => clearInterval(t);
  }, []);

  const realComps = COMPS_POOL.slice(0, 3);

  return (
    <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", background: "#f8fafc", borderTop: "1px solid #eef2f7", borderBottom: "1px solid #eef2f7" }}>
      <SectionHeading
        kicker="Comparable matching"
        accent={BRAND_ACCENT_COLOR}
        title="The AI eyes each comparable for you"
        blurb="For every comp, you get a match score out of five and a plain-English summary of how it stacks up against the subject. Tap one to edit; the agent always has the final word."
      />

      <div style={{ maxWidth: 1180, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "auto 1fr", gap: isMobile ? 22 : 32, alignItems: "stretch" }}>
        {/* Subject card */}
        <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", padding: 14, width: isMobile ? "100%" : 260, boxSizing: "border-box" }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: BRAND_ACCENT_COLOR, textTransform: "uppercase", marginBottom: 8 }}>Subject property</div>
          <div style={{ borderRadius: 12, overflow: "hidden", marginBottom: 10, position: "relative" }}>
            <img src={SUBJECT.photo} alt={SUBJECT.street} style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.55))" }} />
            <div style={{ position: "absolute", left: 10, bottom: 8, color: "#fff", fontSize: 12, fontWeight: 800 }}>{SUBJECT.street}</div>
          </div>
          <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>{SUBJECT.beds} bed · {SUBJECT.baths} bath · {SUBJECT.cars} car</div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{SUBJECT.land}m² · {SUBJECT.suburb}</div>
          <div style={{ marginTop: 10, padding: "8px 10px", background: "#f0fdf4", border: `1px solid ${BRAND_ACCENT_COLOR}44`, borderRadius: 8, fontSize: 11.5, color: "#065f46", fontWeight: 600 }}>
            Target estimate {fmtPrice(PRICE_LOW)} to {fmtPrice(PRICE_HIGH)}
          </div>
        </div>

        {/* Comparables grid with AI scan animation */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 14, position: "relative" }}>
          {realComps.map((c, i) => {
            const scanning = i === scanIdx;
            return (
              <div key={c.id} style={{ background: "#fff", borderRadius: 16, border: `1px solid ${scanning ? BRAND_ACCENT_COLOR : "#e2e8f0"}`, overflow: "hidden", boxShadow: scanning ? `0 14px 32px ${BRAND_ACCENT_COLOR}22` : "0 2px 10px rgba(15,23,42,0.04)", transition: "box-shadow 0.4s ease, border-color 0.4s ease", position: "relative" }}>
                <div style={{ position: "relative", height: 130 }}>
                  <img src={c.photo} alt={c.addr} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  {scanning && (
                    <>
                      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(110deg, transparent 30%, rgba(16,185,129,0.28) 50%, transparent 70%)", backgroundSize: "220px 100%", animation: "shimmer 1.6s linear infinite" }} />
                      <div style={{ position: "absolute", top: 8, left: 8, display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(15,23,42,0.85)", border: `1px solid ${BRAND_ACCENT_COLOR}55`, borderRadius: 999, padding: "3px 8px", fontSize: 10, fontWeight: 700, color: BRAND_ACCENT_COLOR, letterSpacing: 0.3 }}>
                        <span style={{ display: "inline-block", animation: "eyeScan 1.4s ease-in-out infinite" }}>👁</span> Comparing
                      </div>
                    </>
                  )}
                  <div style={{ position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,0.95)", borderRadius: 999, padding: "3px 8px", fontSize: 10.5, fontWeight: 800, color: "#1a1a1a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <MatchStars stars={c.matchStars} size={9} />
                    <span style={{ color: BRAND_COLOR }}>{c.matchStars}/5</span>
                  </div>
                </div>
                <div style={{ padding: "12px 14px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.addr}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: BRAND_COLOR }}>{fmtPrice(c.price)}</div>
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{c.beds} bed · {c.baths} bath · {c.cars} car · {c.land}m² · {c.sold}</div>
                  <div style={{ marginTop: 10, padding: "9px 10px", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 10 }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.2, color: BRAND_ACCENT_COLOR, textTransform: "uppercase", marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <span>👁</span> AI analysis · editable
                    </div>
                    <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.55 }}>{c.aiSummary}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ maxWidth: 1180, margin: "16px auto 0", textAlign: "center", fontSize: 12.5, color: "#64748b" }}>
        That is it. The AI does the comparing; you do the choosing.
      </div>
    </section>
  );
}

/* ─────────── Location analysis ─────────── */
function LocationAnalysisSection({ isMobile }) {
  return (
    <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", background: "#fff" }}>
      <SectionHeading
        kicker="Location analysis"
        accent={BRAND_ACCENT_COLOR}
        title="A suburb story written for this vendor"
        blurb="The AI reads the street, the comparables, and what's nearby, then writes a location analysis the vendor can actually use. Edit any line; everything publishes to the appraisal."
      />

      <div style={{ maxWidth: 1180, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr", gap: isMobile ? 22 : 28, alignItems: "stretch" }}>
        {/* Left: summary + positives/negatives + value factors */}
        <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", boxShadow: "0 2px 16px rgba(15,23,42,0.04)", padding: isMobile ? 18 : 24 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 700, color: BRAND_ACCENT_COLOR, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>
            <span>📍</span> {SUBJECT.suburb} overview
          </div>
          <p style={{ fontSize: 14.5, color: "#374151", lineHeight: 1.7, margin: "0 0 18px 0" }}>{LOCATION_ANALYSIS.summary}</p>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 18 }}>
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, color: "#15803d", textTransform: "uppercase", marginBottom: 8 }}>👍 Positives</div>
              {LOCATION_ANALYSIS.positives.map((p) => (
                <div key={p} style={{ display: "flex", gap: 8, padding: "4px 0", fontSize: 12.5, color: "#065f46" }}>
                  <span style={{ color: BRAND_ACCENT_COLOR, flexShrink: 0 }}>✓</span><span>{p}</span>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, color: "#c2410c", textTransform: "uppercase", marginBottom: 8 }}>🤔 Things to flag</div>
              {LOCATION_ANALYSIS.negatives.map((n) => (
                <div key={n} style={{ display: "flex", gap: 8, padding: "4px 0", fontSize: 12.5, color: "#7c2d12" }}>
                  <span style={{ color: "#f97316", flexShrink: 0 }}>!</span><span>{n}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, color: "#64748b", textTransform: "uppercase", marginBottom: 10 }}>Value factors</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {LOCATION_ANALYSIS.valueFactors.map((v) => {
              const positive = v.impact === "positive";
              return (
                <div key={v.factor} style={{ display: "flex", gap: 10, padding: "10px 12px", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 10 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: positive ? "#dcfce7" : "#fee2e2", color: positive ? "#16a34a" : "#dc2626", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, flexShrink: 0 }}>
                    {positive ? "↑" : "↓"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a1a1a" }}>{v.factor}</div>
                    <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2, lineHeight: 1.5 }}>{v.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: amenities + map-feel */}
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", borderRadius: 20, padding: isMobile ? 18 : 22, color: "#fff", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, color: BRAND_ACCENT_COLOR, textTransform: "uppercase" }}>Nearby amenities</div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>5 within 600m</div>
          </div>

          {/* Stylised map */}
          <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", background: "#0b1224", border: "1px solid rgba(255,255,255,0.08)", height: isMobile ? 170 : 210, marginBottom: 14 }}>
            <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(16,185,129,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.07) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
            {/* Subject pin */}
            <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)" }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: BRAND_BLUE, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 14, boxShadow: `0 0 0 6px ${BRAND_BLUE}33, 0 6px 18px rgba(0,0,0,0.4)`, animation: "pulseGlow 2.2s ease-out infinite" }}>S</div>
              <div style={{ position: "absolute", top: -22, left: "50%", transform: "translateX(-50%)", fontSize: 9.5, fontWeight: 800, color: "#fff", background: BRAND_BLUE, padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap" }}>Subject</div>
            </div>
            {/* Comparable pins (positions are illustrative) */}
            {[{ id: "c1", x: 26, y: 30, label: "C1" }, { id: "c2", x: 70, y: 34, label: "C2" }, { id: "c3", x: 38, y: 74, label: "C3" }].map((p) => (
              <div key={p.id} style={{ position: "absolute", left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%,-50%)", width: 24, height: 24, borderRadius: "50%", background: BRAND_ACCENT_COLOR, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, boxShadow: `0 0 0 4px ${BRAND_ACCENT_COLOR}33` }}>{p.label}</div>
            ))}
            <div style={{ position: "absolute", left: 10, bottom: 10, right: 10, display: "flex", justifyContent: "space-between", fontSize: 9, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: 1 }}>
              <span>Manly</span><span>Queenscliff</span>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {LOCATION_ANALYSIS.amenities.map((a) => (
              <div key={a.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }}>
                <span style={{ fontSize: 16 }}>{a.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#fff" }}>{a.name}</div>
                  <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)" }}>{a.distance}</div>
                </div>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: 0.5 }}>{a.type}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── AI image suggestions: agent editor + vendor view ─────────── */
function AISuggestionsSection({ isMobile }) {
  const [overall, setOverall] = useState(AI_SUGGESTION.overall);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      while (mounted) {
        setPulse(true);
        await new Promise((r) => setTimeout(r, 1200));
        if (!mounted) return;
        setPulse(false);
        for (let i = 0; i < 3; i++) {
          await new Promise((r) => setTimeout(r, 1100));
          if (!mounted) return;
          setFocusedIdx(i);
        }
        await new Promise((r) => setTimeout(r, 1600));
        if (!mounted) return;
        setFocusedIdx(-1);
        await new Promise((r) => setTimeout(r, 1200));
      }
    };
    run();
    return () => { mounted = false; };
  }, []);

  return (
    <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", background: "#fff" }}>
      <SectionHeading
        kicker="AI image suggestions"
        accent={BRAND_ACCENT_COLOR}
        title="Every photo gets its own improvement plan"
        blurb="The AI scores each photo with quick wins, moderate upgrades and major improvements, complete with estimated cost and value uplift. Edit any line before you send."
      />
      <div style={{ maxWidth: 1180, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 26 : 32, alignItems: "stretch" }}>
        {/* AGENT SIDE */}
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", borderRadius: 18, padding: isMobile ? 16 : 22, border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, color: BRAND_ACCENT_COLOR, textTransform: "uppercase" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: BRAND_ACCENT_COLOR, boxShadow: `0 0 0 4px ${BRAND_ACCENT_COLOR}33` }} />
              Agent view · editor
            </div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
              vendorleads.com.au/appraisals
            </div>
          </div>

          <div style={{ borderRadius: 14, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", background: "#0b1224" }}>
            <div style={{ position: "relative", height: isMobile ? 170 : 210 }}>
              <img src={AI_SUGGESTION.imageUrl} alt="Subject front" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              {pulse && (
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(110deg, transparent 30%, rgba(16,185,129,0.25) 50%, transparent 70%)", backgroundSize: "200px 100%", animation: "shimmer 1.6s linear infinite" }} />
              )}
              <div style={{ position: "absolute", top: 10, left: 10, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(15,23,42,0.85)", border: "1px solid rgba(16,185,129,0.45)", padding: "4px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700, color: BRAND_ACCENT_COLOR, letterSpacing: 0.4 }}>
                <span>⚡</span> {pulse ? "Analysing photo." : "AI analysed"}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 12px", background: "linear-gradient(to top, rgba(0,0,0,0.75), transparent)", display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span style={{ background: "rgba(255,255,255,0.92)", padding: "4px 10px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, color: "#1a1a1a" }}>💰 Invest {AI_SUGGESTION.investment}</span>
                <span style={{ background: "rgba(22,163,74,0.92)", padding: "4px 10px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, color: "#fff" }}>↑ {AI_SUGGESTION.uplift} uplift</span>
              </div>
            </div>

            <div style={{ padding: "14px 14px 6px" }}>
              <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.2, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", marginBottom: 6 }}>Overall assessment · editable</div>
              <textarea
                value={overall}
                onChange={(e) => setOverall(e.target.value)}
                rows={3}
                style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "9px 11px", color: "#fff", fontSize: 12.5, lineHeight: 1.55, resize: "none", fontFamily: "inherit", outline: "none" }}
                onFocus={(e) => { e.currentTarget.style.borderColor = BRAND_ACCENT_COLOR; e.currentTarget.style.background = "rgba(16,185,129,0.08)"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
              />
            </div>

            <div style={{ padding: "8px 14px 14px" }}>
              <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.2, color: "#6ee7b7", textTransform: "uppercase", marginBottom: 8 }}>💡 Quick wins · tap to edit</div>
              {AI_SUGGESTION.quickWins.map((qw, i) => {
                const active = focusedIdx === i;
                return (
                  <div key={qw.action} style={{ display: "flex", gap: 10, padding: "9px 11px", marginBottom: 8, background: active ? "rgba(16,185,129,0.14)" : "rgba(255,255,255,0.04)", border: `1px solid ${active ? BRAND_ACCENT_COLOR : "rgba(255,255,255,0.08)"}`, borderRadius: 10, transition: "all 0.3s ease" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", lineHeight: 1.4 }}>
                        {qw.action}
                        {active && <span style={{ display: "inline-block", width: 1, height: 12, background: BRAND_ACCENT_COLOR, marginLeft: 2, verticalAlign: "middle", animation: "caretBlink 0.9s steps(2) infinite" }} />}
                      </div>
                      <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>{qw.impact}</div>
                    </div>
                    <div style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: BRAND_ACCENT_COLOR, alignSelf: "center" }}>{qw.cost}</div>
                  </div>
                );
              })}

              <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1.2, color: "#fbbf24", textTransform: "uppercase", marginBottom: 8, marginTop: 4 }}>🔧 Moderate upgrades</div>
              {AI_SUGGESTION.moderate.map((m, i) => {
                const active = focusedIdx === 2 + i;
                return (
                  <div key={m.action} style={{ display: "flex", gap: 10, padding: "9px 11px", background: active ? "rgba(251,191,36,0.14)" : "rgba(255,255,255,0.04)", border: `1px solid ${active ? "#fbbf24" : "rgba(255,255,255,0.08)"}`, borderRadius: 10, transition: "all 0.3s ease" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", lineHeight: 1.4 }}>
                        {m.action}
                        {active && <span style={{ display: "inline-block", width: 1, height: 12, background: "#fbbf24", marginLeft: 2, verticalAlign: "middle", animation: "caretBlink 0.9s steps(2) infinite" }} />}
                      </div>
                      <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>{m.impact}</div>
                    </div>
                    <div style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: "#fbbf24", alignSelf: "center" }}>{m.cost}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10, fontSize: 11.5, color: "rgba(255,255,255,0.62)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", background: "rgba(16,185,129,0.12)", borderRadius: 999, color: BRAND_ACCENT_COLOR, fontWeight: 700 }}>
              Auto-saved
            </span>
            Every line is yours to override before you send.
          </div>
        </div>

        {/* VENDOR SIDE */}
        <div style={{ background: "#f8fafc", borderRadius: 18, padding: isMobile ? 16 : 22, border: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, color: BRAND_BLUE, textTransform: "uppercase" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: BRAND_BLUE, boxShadow: `0 0 0 4px ${BRAND_BLUE}33` }} />
              Vendor view · published
            </div>
            <div style={{ fontSize: 10.5, color: "#94a3b8", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>homeprice.site/appraisal</div>
          </div>

          <div style={{ background: "#fff", borderRadius: 20, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
            <div style={{ position: "relative" }}>
              <img src={AI_SUGGESTION.imageUrl} alt="Subject front" style={{ width: "100%", height: isMobile ? 180 : 220, objectFit: "cover", display: "block" }} />
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "55%", background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 100%)", pointerEvents: "none" }} />
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: isMobile ? "12px 14px" : "14px 18px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ background: "rgba(255,255,255,0.95)", padding: "5px 11px", borderRadius: 20, fontSize: 11.5, fontWeight: 600, color: "#1a1a1a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span style={{ color: BRAND_ACCENT_COLOR }}>$</span> Invest {AI_SUGGESTION.investment}
                  </span>
                  <span style={{ background: "rgba(22,163,74,0.92)", padding: "5px 11px", borderRadius: 20, fontSize: 11.5, fontWeight: 600, color: "#fff" }}>
                    ↑ {AI_SUGGESTION.uplift} uplift
                  </span>
                </div>
              </div>
            </div>
            <div style={{ padding: isMobile ? "18px 16px" : "22px 24px" }}>
              <p style={{ fontSize: 13.5, color: "#4b5563", lineHeight: 1.7, margin: "0 0 16px", padding: "0 0 14px", borderBottom: "1px solid #f0eeeb" }}>
                {overall}
              </p>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 600, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
                💡 Quick Wins
              </div>
              {AI_SUGGESTION.quickWins.map((qw) => (
                <div key={qw.action} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid #f5f4f1", alignItems: "flex-start" }}>
                  <span style={{ width: 16, height: 16, borderRadius: 4, background: "#dcfce7", color: "#16a34a", fontSize: 10, fontWeight: 900, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>✓</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#1a1a1a", lineHeight: 1.5 }}>{qw.action}</div>
                    <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2, lineHeight: 1.5 }}>{qw.impact}</div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a1a", flexShrink: 0, textAlign: "right" }}>
                    {qw.cost}
                    <div style={{ fontSize: 9.5, fontWeight: 600, color: "#16a34a", background: "#dcfce7", padding: "2px 7px", borderRadius: 10, marginTop: 3, display: "inline-block", textTransform: "uppercase", letterSpacing: 0.5 }}>Low effort</div>
                  </div>
                </div>
              ))}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 600, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 14, marginBottom: 10 }}>
                🔧 Moderate Upgrades
              </div>
              {AI_SUGGESTION.moderate.map((m) => (
                <div key={m.action} style={{ display: "flex", gap: 12, padding: "8px 0", alignItems: "flex-start" }}>
                  <span style={{ width: 16, height: 16, borderRadius: 4, background: "#fef3c7", color: "#f59e0b", fontSize: 10, fontWeight: 900, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>✓</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#1a1a1a", lineHeight: 1.5 }}>{m.action}</div>
                    <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2, lineHeight: 1.5 }}>{m.impact}</div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a1a", flexShrink: 0, textAlign: "right" }}>
                    {m.cost}
                    <div style={{ fontSize: 9.5, fontWeight: 600, color: "#f59e0b", background: "#fef3c7", padding: "2px 7px", borderRadius: 10, marginTop: 3, display: "inline-block", textTransform: "uppercase", letterSpacing: 0.5 }}>Mod. effort</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 12, fontSize: 11.5, color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
            <span>↻</span> The vendor sees exactly what you signed off on; every edit syncs to the published page.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Workflow ─────────── */
const WORKFLOW_STEPS = [
  { kicker: "Step 1", title: "Pick the best comparables", blurb: "Comps load automatically with match scores. Tap to include or exclude. Add a one-liner if you want." },
  { kicker: "Step 2", title: "Range narrows automatically", blurb: "Low and high estimates tighten as you curate. Override the numbers if you disagree." },
  { kicker: "Step 3", title: "Send the report", blurb: "One link via SMS or email. Branded, mobile-first, and lets the vendor book straight in." },
];

function WorkflowStepFrame({ children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 20px 50px rgba(0,0,0,0.35)", width: "100%", minWidth: 0 }}>
      <div style={{ background: "#f1f5f9", padding: "8px 12px", display: "flex", alignItems: "center", gap: 6, borderBottom: "1px solid #e2e8f0" }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fca5a5", flexShrink: 0 }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fcd34d", flexShrink: 0 }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#86efac", flexShrink: 0 }} />
        <div style={{ marginLeft: 10, flex: 1, minWidth: 0, background: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 10.5, color: "#64748b", border: "1px solid #e2e8f0", fontFamily: "ui-monospace, SFMono-Regular, monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          www.vendorleads.com.au/property-appraisal
        </div>
      </div>
      <div style={{ padding: 16, height: 380, overflow: "hidden" }}>{children}</div>
    </div>
  );
}

function WfPick() {
  const [picked, setPicked] = useState([]);
  useEffect(() => {
    let i = 0;
    const order = ["c1", "c2", "c3"];
    const t = setInterval(() => {
      setPicked((p) => { const next = order[i]; i = (i + 1) % (order.length + 2); if (i <= order.length) return [...p.filter((x) => x !== next), next]; return []; });
    }, 1100);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Pick comparables</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, maxHeight: 320, overflow: "hidden" }}>
        {COMPS_POOL.slice(0, 4).map((c) => {
          const on = picked.includes(c.id);
          return (
            <div key={c.id} style={{ border: `1.5px solid ${on ? BRAND_ACCENT_COLOR : "#e5e7eb"}`, background: on ? "#ecfdf5" : "#fff", borderRadius: 8, padding: 8, display: "flex", gap: 8, transition: "all 0.3s" }}>
              <img src={c.photo} alt={c.addr} style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 6 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.addr}</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>{c.beds}·{c.baths}·{c.cars}</div>
                <div style={{ fontSize: 11, fontWeight: 800, color: BRAND_COLOR, marginTop: 2 }}>{fmtPrice(c.price)}</div>
              </div>
              <div style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${on ? BRAND_ACCENT_COLOR : "#cbd5e1"}`, background: on ? BRAND_ACCENT_COLOR : "#fff", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, flexShrink: 0, alignSelf: "flex-start" }}>
                {on ? "✓" : ""}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WfRange() {
  const [range, setRange] = useState({ lo: 2_000_000, hi: 3_500_000 });
  useEffect(() => {
    const targets = [
      { lo: 2_000_000, hi: 3_500_000 },
      { lo: 2_150_000, hi: 3_000_000 },
      { lo: 2_250_000, hi: 2_800_000 },
      { lo: PRICE_LOW, hi: PRICE_HIGH },
    ];
    let i = 0;
    const t = setInterval(() => { i = (i + 1) % targets.length; setRange(targets[i]); }, 1400);
    return () => clearInterval(t);
  }, []);
  const loPct = ((range.lo - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * 100;
  const hiPct = ((range.hi - RANGE_MIN) / (RANGE_MAX - RANGE_MIN)) * 100;
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 10 }}>Estimated range</div>
      <div style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)", borderRadius: 14, padding: "18px 18px 20px 18px", color: "#fff" }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: "rgba(255,255,255,0.6)" }}>6 GOLF PARADE · ESTIMATED VALUE</div>
        <div style={{ fontSize: 26, fontWeight: 900, marginTop: 6, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
          {fmtPrice(range.lo)} <span style={{ color: BRAND_ACCENT_COLOR }}>to</span> {fmtPrice(range.hi)}
        </div>
        <div style={{ marginTop: 16, height: 12, background: "rgba(255,255,255,0.1)", borderRadius: 6, position: "relative" }}>
          <div style={{ position: "absolute", top: 0, bottom: 0, left: `${loPct}%`, right: `${100 - hiPct}%`, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_ACCENT_COLOR})`, borderRadius: 6, transition: "left 1s ease, right 1s ease" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: "rgba(255,255,255,0.5)" }}>
          <span>{fmtPrice(RANGE_MIN)}</span><span>{fmtPrice((RANGE_MIN + RANGE_MAX) / 2)}</span><span>{fmtPrice(RANGE_MAX)}</span>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Chip icon="🎯" label="HIGH CONFIDENCE" />
          <Chip icon="📍" label="LOCAL DATA" colour={BRAND_BLUE} />
          <Chip icon="⚡" label="LIVE" colour="#f59e0b" />
        </div>
      </div>
    </div>
  );
}

function WfSend() {
  const [sent, setSent] = useState(false);
  useEffect(() => { const t = setInterval(() => setSent((s) => !s), 2400); return () => clearInterval(t); }, []);
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 10 }}>Send to vendor</div>
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 14px", background: "#fff", marginBottom: 10 }}>
        <div style={{ fontSize: 10.5, color: "#64748b", marginBottom: 4 }}>Recipient</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Anita Patel · 0411 222 333</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <div style={{ border: `1.5px solid ${BRAND_BLUE}`, background: "#eff6ff", borderRadius: 10, padding: "12px 10px", textAlign: "center", fontWeight: 700, fontSize: 12, color: BRAND_BLUE }}>📱 SMS link</div>
        <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "12px 10px", textAlign: "center", fontWeight: 700, fontSize: 12, color: "#475569" }}>✉ Email</div>
      </div>
      <button style={{ width: "100%", padding: "12px", background: sent ? "#065f46" : BRAND_ACCENT_COLOR, color: "#fff", border: "none", borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: "pointer", boxShadow: "0 6px 16px rgba(16,185,129,0.4)", transition: "background 0.3s" }}>
        {sent ? "✓ Sent, vendor opened just now" : "Send appraisal link"}
      </button>
      {sent && (
        <div style={{ marginTop: 10, padding: "8px 10px", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8, fontSize: 11.5, color: "#065f46", animation: "slideInDown 0.4s ease both" }}>
          📬 Delivered · 🔵 Opened on iPhone · 📅 Meeting scheduled
        </div>
      )}
    </div>
  );
}

function WorkflowAnimation({ isMobile }) {
  const [step, setStep] = useState(0);
  useEffect(() => { const t = setInterval(() => setStep((s) => (s + 1) % WORKFLOW_STEPS.length), 5800); return () => clearInterval(t); }, []);
  const Body = [WfPick, WfRange, WfSend][step];

  return (
    <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", borderRadius: 16, padding: isMobile ? "1.5rem 1.25rem" : "2.25rem 2rem", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" }} />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.2fr", gap: isMobile ? 22 : 36, position: "relative", zIndex: 1 }}>
        <div>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 800, letterSpacing: 1.5, marginBottom: 16, textTransform: "uppercase" }}>The workflow</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {WORKFLOW_STEPS.map((s, i) => {
              const active = i === step;
              return (
                <button key={s.title} onClick={() => setStep(i)} style={{ background: active ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.04)", border: `1px solid ${active ? BRAND_ACCENT_COLOR : "rgba(255,255,255,0.08)"}`, borderRadius: 12, padding: "13px 15px", textAlign: "left", cursor: "pointer", transition: "all 0.3s", display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: active ? BRAND_ACCENT_COLOR : "rgba(255,255,255,0.1)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: active ? BRAND_ACCENT_COLOR : "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>{s.kicker}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", margin: "2px 0 3px" }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>{s.blurb}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 800, letterSpacing: 1.5, marginBottom: 12, textTransform: "uppercase" }}>Live preview · step {step + 1} of {WORKFLOW_STEPS.length}</div>
          <WorkflowStepFrame><div key={step}><Body /></div></WorkflowStepFrame>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Results panel (no "more listings" stat) ─────────── */
function ResultsPanel({ isMobile }) {
  const data = [
    { m: "Jan", before: 2, after: 3 },
    { m: "Feb", before: 3, after: 4 },
    { m: "Mar", before: 1, after: 5 },
    { m: "Apr", before: 2, after: 4 },
    { m: "May", before: 3, after: 6 },
    { m: "Jun", before: 2, after: 7 },
  ];
  const max = 9;
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.3fr 1fr", gap: 22 }}>
      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", padding: isMobile ? 18 : 26, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: BRAND_COLOR }}>Listings won per month</div>
          <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#64748b" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: "#cbd5e1" }} />Before
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: BRAND_ACCENT_COLOR }} />With Vendorleads
            </span>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${data.length}, 1fr)`, gap: 10, alignItems: "end", height: 220, borderBottom: "1px solid #f1f5f9", padding: "0 4px 6px" }}>
          {data.map((d, i) => (
            <div key={d.m} style={{ display: "flex", gap: 4, alignItems: "end", justifyContent: "center", height: "100%" }}>
              <div style={{ width: "40%", height: `${(d.before / max) * 100}%`, background: "#cbd5e1", borderRadius: "4px 4px 0 0", transformOrigin: "bottom", animation: `barGrow 0.6s ${i * 60}ms ease both` }} />
              <div style={{ width: "40%", height: `${(d.after / max) * 100}%`, background: BRAND_ACCENT_COLOR, borderRadius: "4px 4px 0 0", transformOrigin: "bottom", animation: `barGrow 0.6s ${i * 60 + 200}ms ease both`, boxShadow: "0 -2px 8px rgba(16,185,129,0.2)" }} />
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${data.length}, 1fr)`, gap: 10, marginTop: 6 }}>
          {data.map((d) => (
            <div key={d.m} style={{ fontSize: 10.5, color: "#94a3b8", textAlign: "center", fontWeight: 600 }}>{d.m}</div>
          ))}
        </div>
      </div>
      <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #134e4a 100%)", borderRadius: 16, padding: isMobile ? 18 : 26, color: "#fff" }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", marginBottom: 14 }}>Agents report</div>
        {[
          ["94%", "of vendors open the link within the first hour"],
          ["~7 min", "average time to send the first appraisal"],
        ].map(([stat, copy]) => (
          <div key={copy} style={{ padding: "14px 0", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: BRAND_ACCENT_COLOR, letterSpacing: "-0.02em", minWidth: 80 }}>{stat}</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>{copy}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── Tracking section ─────────── */
const TRACKING_EVENTS = [
  { icon: "📬", label: "Delivered via SMS", meta: "to Anita at 9:41am", colour: BRAND_BLUE },
  { icon: "📲", label: "Opened on iPhone", meta: "9:43am, 2 min after delivery", colour: BRAND_BLUE },
  { icon: "📊", label: "Viewed estimated range", meta: "Dwelt 28s on the $2.3m to $2.7m card", colour: "#a78bfa" },
  { icon: "🏠", label: "Scrolled comparables", meta: "Tapped 75 Whistler Street for details", colour: BRAND_ACCENT_COLOR },
  { icon: "💡", label: "Read improvement suggestions", meta: "Lingered on the quick-wins section", colour: "#fbbf24" },
  { icon: "👀", label: "Re-opened the next morning", meta: "10:12am, still on the range card", colour: BRAND_BLUE },
  { icon: "📅", label: "Tapped 'Book a meeting'", meta: "Saturday 10:30am, calendar invite sent", colour: BRAND_ACCENT_COLOR },
];

const HEAT_SECTIONS = [
  { label: "Hero / address", dwell: 6, intensity: 0.3 },
  { label: "Estimated range", dwell: 42, intensity: 1.0 },
  { label: "Comparable sales", dwell: 31, intensity: 0.78 },
  { label: "Location analysis", dwell: 18, intensity: 0.48 },
  { label: "Improvement suggestions", dwell: 26, intensity: 0.62 },
  { label: "Book a meeting", dwell: 4, intensity: 0.92 },
];

function TrackingSection({ isMobile }) {
  const [visible, setVisible] = useState(1);
  useEffect(() => {
    const t = setInterval(() => setVisible((v) => (v >= TRACKING_EVENTS.length ? 1 : v + 1)), 1400);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "60px 60px", pointerEvents: "none" }} />
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 2.5rem auto" }}>
          <div style={{ display: "inline-block", fontSize: 11, fontWeight: 800, letterSpacing: 2, color: BRAND_ACCENT_COLOR, background: `${BRAND_ACCENT_COLOR}1a`, padding: "4px 12px", borderRadius: 999, marginBottom: 12, textTransform: "uppercase" }}>
            Everything is tracked
          </div>
          <h2 style={{ fontSize: "1.9rem", fontWeight: 800, color: "#fff", margin: "0 0 0.75rem 0", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
            You'll know the moment the vendor is warm
          </h2>
          <p style={{ color: "rgba(255,255,255,0.72)", fontSize: "1rem", lineHeight: 1.65, margin: 0 }}>
            Every open, scroll, dwell second and tap on the appraisal is captured. Walk into the listing pitch already knowing what they cared about most.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr", gap: isMobile ? 22 : 28 }}>
          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: isMobile ? 18 : 22, position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, color: "rgba(255,255,255,0.7)", textTransform: "uppercase" }}>Engagement timeline</div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 700, color: BRAND_ACCENT_COLOR }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: BRAND_ACCENT_COLOR, boxShadow: `0 0 0 4px ${BRAND_ACCENT_COLOR}33`, animation: "pulseGlow 2s ease-out infinite" }} />
                Live
              </div>
            </div>
            <div style={{ position: "relative", paddingLeft: 22 }}>
              <div style={{ position: "absolute", left: 9, top: 4, bottom: 4, width: 2, background: "rgba(255,255,255,0.08)" }} />
              {TRACKING_EVENTS.map((ev, i) => {
                const isOn = i < visible;
                return (
                  <div key={ev.label} style={{ position: "relative", padding: "8px 0 10px 18px", opacity: isOn ? 1 : 0.35, transition: "opacity 0.5s ease" }}>
                    <div style={{ position: "absolute", left: -22, top: 8, width: 20, height: 20, borderRadius: "50%", background: isOn ? ev.colour : "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, boxShadow: isOn ? `0 0 0 4px ${ev.colour}22` : "none", transition: "all 0.4s ease", animation: isOn && i === visible - 1 ? "tickPop 0.4s ease both" : "none" }}>
                      <span>{ev.icon}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{ev.label}</div>
                    <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{ev.meta}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: isMobile ? 18 : 22 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", marginBottom: 16 }}>What they actually read</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {HEAT_SECTIONS.map((s) => (
                <div key={s.label}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>{s.label}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)", fontVariantNumeric: "tabular-nums" }}>{s.dwell}s</div>
                  </div>
                  <div style={{ height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden", position: "relative" }}>
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${s.intensity * 100}%`, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_ACCENT_COLOR})`, borderRadius: 4, boxShadow: `0 0 12px ${BRAND_ACCENT_COLOR}55`, animation: "slideInLeft 0.6s ease both" }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 18, padding: "12px 14px", background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 10, fontSize: 12, color: "#a7f3d0", lineHeight: 1.55 }}>
              <span style={{ fontWeight: 800 }}>🔥 Hot signal:</span> Anita re-opened the range card three times. Lead with price reassurance at the meeting.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── FAQ ─────────── */
function Faq({ isMobile }) {
  const items = [
    { q: "Where does the comparable sales data come from?", a: "We pull recent sales from local data feeds and the AI scores each one against the subject by beds, baths, land size, distance and recency, then writes a plain-English summary you can edit." },
    { q: "How do the AI image suggestions work?", a: "Each photo you upload is analysed automatically. The AI returns an overall assessment plus quick wins, moderate upgrades and major improvements with estimated cost and value uplift. Every line is editable before the report is sent." },
    { q: "Where does the location analysis come from?", a: "The AI reads the street, the comparables and what is nearby, then drafts a suburb summary with positives, things to flag, value factors and amenities. Tweak anything; it publishes straight to the appraisal." },
    { q: "Can I override the range?", a: "Yes. The system suggests a tight range based on the comps you pick, but you can drag the slider, type a custom range, or pin to a specific price." },
    { q: "Will I know if the vendor opened it?", a: "Yes. You see every open, every section they read, how long they dwelt, and when they tapped to book. Walk into the listing pitch already knowing what mattered most." },
    { q: "Does it work on a phone in the car?", a: "Yes. The whole build flow is mobile-first. Most agents finish their first appraisal between the inspection and the next." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      {items.map((it, i) => {
        const isOpen = i === open;
        return (
          <div key={it.q} style={{ borderTop: i === 0 ? "1px solid #e5e7eb" : "none", borderBottom: "1px solid #e5e7eb", padding: "14px 0" }}>
            <button onClick={() => setOpen(isOpen ? -1 : i)} style={{ width: "100%", background: "none", border: "none", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, padding: 0, fontSize: isMobile ? 14.5 : 16, fontWeight: 700, color: BRAND_COLOR }}>
              {it.q}
              <span style={{ fontSize: 18, color: BRAND_ACCENT_COLOR, transform: isOpen ? "rotate(45deg)" : "rotate(0)", transition: "transform 0.25s", display: "inline-block" }}>+</span>
            </button>
            {isOpen && (
              <div style={{ marginTop: 8, color: GRAY_DARK_COLOR, fontSize: 14, lineHeight: 1.65, animation: "fadeUp 0.3s ease both" }}>{it.a}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────── Main ─────────── */
export default function PropertyAppraisalFeature() {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  const [showContact, setShowContact] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openSignIn = () => setShowSignIn(true);

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", background: "#fff" }}>
      <GlobalAnimations />
      <Navbar onSignIn={openSignIn} />

      <Hero isMobile={isMobile} onCta={openSignIn} />

      <BeforeAfter isMobile={isMobile} />

      <ReportAnatomy isMobile={isMobile} />

      <ComparableMatchSection isMobile={isMobile} />

      <LocationAnalysisSection isMobile={isMobile} />

      <AISuggestionsSection isMobile={isMobile} />

      <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", background: "#f8fafc", borderTop: "1px solid #eef2f7", borderBottom: "1px solid #eef2f7" }}>
        <SectionHeading kicker="How it works" accent={BRAND_ACCENT_COLOR} title="Address to appraisal in three taps" blurb="Inside the dashboard, building and sending a CMA takes about a coffee's worth of time. Here is the exact flow." />
        <div style={{ maxWidth: 1100, margin: "0 auto" }}><WorkflowAnimation isMobile={isMobile} /></div>
      </section>

      <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", background: "#fff" }}>
        <SectionHeading kicker="What changes" accent={BRAND_ACCENT_COLOR} title="Listings start going your way" blurb="When you turn up to the pitch with a report the vendor has already read and engaged with, the conversation starts ten steps ahead." />
        <div style={{ maxWidth: 1100, margin: "0 auto" }}><ResultsPanel isMobile={isMobile} /></div>
      </section>

      <TrackingSection isMobile={isMobile} />

      <TestimonialSection isMobile={isMobile} testimonial="barbara" />

      <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", background: "#fff", borderTop: "1px solid #eef2f7" }}>
        <SectionHeading kicker="FAQ" accent={BRAND_ACCENT_COLOR} title="The questions we hear most" blurb="If something else is on your mind, the team is one click away." />
        <Faq isMobile={isMobile} />
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <button onClick={() => setShowContact(true)} style={{ border: `1.5px solid ${BRAND_COLOR}`, borderRadius: 8, padding: "0.85rem 1.8rem", background: "#fff", color: BRAND_COLOR, fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" }}>
            Talk to the team
          </button>
        </div>
      </section>

      <section style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)", padding: isMobile ? "3.5rem 1.5rem" : "5rem 2rem", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "60px 60px", pointerEvents: "none" }} />
        <div style={{ maxWidth: 640, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <h2 style={{ fontSize: isMobile ? "1.8rem" : "2.4rem", fontWeight: 800, color: "#fff", marginBottom: "1rem", lineHeight: 1.2, letterSpacing: "-0.02em" }}>
            Your next listing is one beautiful appraisal away.
          </h2>
          <p style={{ color: "rgba(255,255,255,0.7)", marginBottom: "2rem", fontSize: "1.1rem", lineHeight: 1.6 }}>
            Build one in seven minutes. Send it from your phone. Get a meeting on the calendar before the kettle boils.
          </p>
          <button onClick={openSignIn} style={{ border: "none", borderRadius: 8, padding: "0.95rem 2.5rem", fontSize: "1.05rem", fontWeight: 700, cursor: "pointer", background: BRAND_ACCENT_COLOR, color: "#fff", boxShadow: "0 4px 14px rgba(16,185,129,0.4)", transition: "transform 0.15s, box-shadow 0.15s" }}
            onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.55)"; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)"; }}>
            Build my first appraisal
          </button>
        </div>
      </section>

      {showSignIn && <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />}
      {showContact && <ContactVendorleadsModal show={showContact} onHide={() => setShowContact(false)} />}
    </div>
  );
}
